// Lightweight Web Audio synth: no external assets required.
// All sounds are procedurally generated tones/noise.

let ctx = null;
let masterGain = null;
let ambientNode = null;
let muted = false;

function ensureCtx() {
  if (ctx) return ctx;
  try {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.35;
    masterGain.connect(ctx.destination);
  } catch (e) {
    console.warn("AudioContext unavailable", e);
  }
  return ctx;
}

function resume() {
  ensureCtx();
  if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
}

export function setMuted(m) {
  muted = m;
  if (masterGain) masterGain.gain.value = m ? 0 : 0.35;
}
export function isMuted() {
  return muted;
}

function envelope(g, t, attack, decay, sustain, sustainLevel, release) {
  const now = t;
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(1, now + attack);
  g.gain.linearRampToValueAtTime(sustainLevel, now + attack + decay);
  g.gain.setValueAtTime(sustainLevel, now + attack + decay + sustain);
  g.gain.linearRampToValueAtTime(0, now + attack + decay + sustain + release);
}

function playTone({ freq = 440, type = "sine", dur = 0.15, vol = 0.3, glideTo = null }) {
  const c = ensureCtx();
  if (!c || muted) return;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  if (glideTo != null) {
    osc.frequency.linearRampToValueAtTime(glideTo, c.currentTime + dur);
  }
  osc.connect(g).connect(masterGain);
  envelope(g, c.currentTime, 0.005, 0.02, dur - 0.05, vol, 0.03);
  osc.start();
  osc.stop(c.currentTime + dur + 0.05);
}

function playNoise({ dur = 0.4, vol = 0.25, filter = 1200, sweep = null }) {
  const c = ensureCtx();
  if (!c || muted) return;
  const bufSize = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, bufSize, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1) * 0.9;
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = "lowpass";
  bp.frequency.value = filter;
  if (sweep) bp.frequency.linearRampToValueAtTime(sweep, c.currentTime + dur);
  const g = c.createGain();
  src.connect(bp).connect(g).connect(masterGain);
  envelope(g, c.currentTime, 0.01, 0.05, dur - 0.1, vol, 0.05);
  src.start();
  src.stop(c.currentTime + dur + 0.05);
}

// ------ Public SFX -------
export function sfxClick() {
  resume();
  playTone({ freq: 880, type: "square", dur: 0.06, vol: 0.15 });
}

export function sfxPlace() {
  resume();
  playTone({ freq: 600, type: "sine", dur: 0.14, vol: 0.25, glideTo: 320 });
  setTimeout(() => playTone({ freq: 220, type: "triangle", dur: 0.1, vol: 0.15 }), 90);
}

export function sfxError() {
  resume();
  playTone({ freq: 220, type: "sawtooth", dur: 0.16, vol: 0.2, glideTo: 140 });
}

export function sfxInfo() {
  resume();
  playTone({ freq: 820, type: "sine", dur: 0.09, vol: 0.16 });
  setTimeout(() => playTone({ freq: 1240, type: "sine", dur: 0.09, vol: 0.16 }), 80);
}

export function sfxWarning() {
  resume();
  playTone({ freq: 660, type: "square", dur: 0.14, vol: 0.2 });
  setTimeout(() => playTone({ freq: 440, type: "square", dur: 0.14, vol: 0.2 }), 160);
}

export function sfxCritical() {
  resume();
  for (let i = 0; i < 3; i++) {
    setTimeout(() => {
      playTone({ freq: 320, type: "sawtooth", dur: 0.1, vol: 0.25 });
      setTimeout(() => playTone({ freq: 200, type: "sawtooth", dur: 0.1, vol: 0.25 }), 110);
    }, i * 260);
  }
}

export function sfxRocket() {
  resume();
  playNoise({ dur: 1.8, vol: 0.3, filter: 400, sweep: 2000 });
  playTone({ freq: 90, type: "sawtooth", dur: 1.6, vol: 0.15, glideTo: 40 });
}

export function sfxStormStart() {
  resume();
  playNoise({ dur: 2.2, vol: 0.4, filter: 200, sweep: 900 });
  playTone({ freq: 60, type: "sine", dur: 2.0, vol: 0.15, glideTo: 40 });
}

// ------ Ambient wind loop -------
export function startAmbient() {
  const c = ensureCtx();
  if (!c || ambientNode || muted) return;
  const bufSize = Math.floor(c.sampleRate * 4);
  const buf = c.createBuffer(1, bufSize, c.sampleRate);
  const data = buf.getChannelData(0);
  // brown noise
  let last = 0;
  for (let i = 0; i < bufSize; i++) {
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    data[i] = last * 3.2;
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  src.loop = true;
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 500;
  const g = c.createGain();
  g.gain.value = 0.09;
  // slow LFO to modulate volume (breathy wind)
  const lfo = c.createOscillator();
  const lfoGain = c.createGain();
  lfo.frequency.value = 0.12;
  lfoGain.gain.value = 0.05;
  lfo.connect(lfoGain).connect(g.gain);
  src.connect(lp).connect(g).connect(masterGain);
  src.start();
  lfo.start();
  ambientNode = { src, lfo, g };
}

export function stopAmbient() {
  if (!ambientNode) return;
  try {
    ambientNode.src.stop();
    ambientNode.lfo.stop();
  } catch (e) {}
  ambientNode = null;
}

export function initAudioOnFirstGesture() {
  const kick = () => {
    resume();
    startAmbient();
    startAmbientMusic();
    window.removeEventListener("click", kick);
    window.removeEventListener("keydown", kick);
  };
  window.addEventListener("click", kick);
  window.addEventListener("keydown", kick);
}

// ------ Ambient Music Drone -------
let ambientMusicNodes = null;
let ambientMusicVolume = 0.5;

export function setMusicVolume(v) {
  ambientMusicVolume = Math.max(0, Math.min(1, Number(v) || 0));
  if (ambientMusicNodes && ambientMusicNodes.musicGain) {
    ambientMusicNodes.musicGain.gain.setValueAtTime(
      ambientMusicVolume,
      ctx ? ctx.currentTime : 0
    );
  }
}

export function startAmbientMusic() {
  resume();
  const c = ensureCtx();
  if (!c || ambientMusicNodes || muted) return;

  try {
    // Primary oscillator: low frequency sine wave (~55Hz)
    const osc1 = c.createOscillator();
    osc1.type = "sine";
    osc1.frequency.value = 55;

    const osc1Gain = c.createGain();
    osc1Gain.gain.value = 0.05;

    // Secondary oscillator: ~110Hz sine wave at very low gain (0.02)
    const osc2 = c.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = 110;

    const osc2Gain = c.createGain();
    osc2Gain.gain.value = 0.02;

    // Slow LFO for subtle volume modulation (~0.08Hz)
    const lfo = c.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 0.08;

    const lfoGain = c.createGain();
    lfoGain.gain.value = 0.015;

    // Connect LFO to modulate osc1Gain
    lfo.connect(lfoGain);
    lfoGain.connect(osc1Gain.gain);

    // Master volume gain for ambient music
    const musicGain = c.createGain();
    musicGain.gain.value = ambientMusicVolume;

    // Connect audio graph
    osc1.connect(osc1Gain).connect(musicGain);
    osc2.connect(osc2Gain).connect(musicGain);
    musicGain.connect(masterGain);

    osc1.start();
    osc2.start();
    lfo.start();

    ambientMusicNodes = { osc1, osc2, osc1Gain, osc2Gain, lfo, lfoGain, musicGain };
  } catch (e) {
    console.warn("Error starting ambient music", e);
  }
}

export function stopAmbientMusic() {
  if (!ambientMusicNodes) return;
  try {
    const { osc1, osc2, lfo, osc1Gain, osc2Gain, lfoGain, musicGain } = ambientMusicNodes;
    if (osc1) {
      osc1.stop();
      osc1.disconnect();
    }
    if (osc2) {
      osc2.stop();
      osc2.disconnect();
    }
    if (lfo) {
      lfo.stop();
      lfo.disconnect();
    }
    if (osc1Gain) osc1Gain.disconnect();
    if (osc2Gain) osc2Gain.disconnect();
    if (lfoGain) lfoGain.disconnect();
    if (musicGain) musicGain.disconnect();
  } catch (e) {
    console.warn("Error stopping ambient music", e);
  }
  ambientMusicNodes = null;
}
